import { useEffect, useMemo, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { api, API } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronLeft, ChevronRight, BookOpen } from "lucide-react";

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export default function Reader() {
  const { bookId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [width, setWidth] = useState(720);

  useEffect(() => {
    api.get(`/books/${bookId}/read`).then(({ data }) => setData(data)).catch((e) => setError(e.response?.data?.detail || "Access denied"));
  }, [bookId]);

  useEffect(() => {
    const resize = () => setWidth(Math.min(900, window.innerWidth - 48));
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, []);

  const pdfSrc = useMemo(() => {
    if (!data?.book?.pdf_url) return null;
    const raw = data.book.pdf_url;
    if (raw.startsWith("http")) return raw;
    // If it's a relative /api/files/... path, prefix with the API host
    return raw.startsWith("/api/") ? API.replace(/\/api$/, "") + raw : raw;
  }, [data]);

  if (!user) return <Navigate to="/" replace />;

  if (error) return (
    <div className="max-w-3xl mx-auto p-10 text-center">
      <p className="text-[#DC2626] text-lg">{error}</p>
      <Link to="/library"><Button variant="outline" className="mt-4">Back to Library</Button></Link>
    </div>
  );

  if (!data) return <div className="p-10 text-center text-[#6E6259]">Loading...</div>;

  const wm = data.watermark;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
      <Link to="/library" className="inline-flex items-center gap-1 text-[#5C061C] mb-3" data-testid="reader-back">
        <ArrowLeft className="h-4 w-4" /> Back to Library
      </Link>
      <h1 className="font-serif text-3xl text-[#5C061C]">{data.book.title}</h1>
      <p className="text-sm text-[#6E6259]">Rental expires: {new Date(data.rental.expires_at).toLocaleString()}</p>

      {pdfSrc ? (
        <>
          <div className="mt-6 relative no-select overflow-hidden rounded-2xl card-warm" data-testid="pdf-reader-viewer" onContextMenu={(e) => e.preventDefault()}>
            <div className="relative grid place-items-center bg-[#F4EEDF] p-4">
              <Document
                file={pdfSrc}
                onLoadSuccess={({ numPages }) => setNumPages(numPages)}
                onLoadError={() => setError("Could not load PDF")}
                loading={<div className="p-8 text-[#6E6259]">Preparing sacred text...</div>}
              >
                <Page pageNumber={page} width={width} renderTextLayer={false} renderAnnotationLayer={false} />
              </Document>
              {/* Watermark overlay */}
              <div className="watermark-layer">
                <div className="watermark-grid">
                  {Array.from({ length: 20 }).map((_, i) => (
                    <div key={i} className="whitespace-nowrap">{wm} · Shri Vidya Sadhana</div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between mt-4">
            <Button variant="outline" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1} className="border-[#5C061C] text-[#5C061C]" data-testid="reader-prev">
              <ChevronLeft className="h-4 w-4 mr-1" /> Prev
            </Button>
            <span className="text-sm text-[#5C061C] font-semibold" data-testid="reader-page-indicator">Page {page} of {numPages || "…"}</span>
            <Button variant="outline" onClick={() => setPage((p) => Math.min(numPages, p + 1))} disabled={!numPages || page >= numPages} className="border-[#5C061C] text-[#5C061C]" data-testid="reader-next">
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          <p className="text-center text-xs text-[#6E6259] mt-3">Watermarked to {wm} · Screenshots are traceable · This copy is licensed to you only</p>
        </>
      ) : (
        // No PDF uploaded yet — fall back to rich placeholder
        <div className="relative mt-6 card-warm rounded-2xl p-8 sm:p-12 no-select overflow-hidden min-h-[60vh]" onContextMenu={(e) => e.preventDefault()}>
          <div className="watermark-layer">
            <div className="watermark-grid">
              {Array.from({ length: 24 }).map((_, i) => (
                <div key={i} className="whitespace-nowrap">{wm} · Sri Vidya Saadhna</div>
              ))}
            </div>
          </div>
          <article className="relative prose max-w-none text-[#1F1A17]" style={{ zIndex: 2 }}>
            <div className="flex items-center gap-2 text-[#B45309] mb-3"><BookOpen className="h-4 w-4" /><span className="text-xs uppercase tracking-widest">Preview</span></div>
            <p className="font-serif text-2xl text-[#5C061C] text-center">॥ ॐ श्री गुरुभ्यो नमः ॥</p>
            <p className="mt-6 leading-relaxed">
              {data.book.description || "This book's PDF has not been uploaded by the admin yet. Once uploaded, the full sacred text will stream here page-by-page with your mobile-number watermark for the entire rental period."}
            </p>
          </article>
        </div>
      )}
    </div>
  );
}
